<template>
    <BRow>
        <BCol cols="12">
            <BCard class="mb-3 border-info shadow-sm">
                <template #header>
                    <h5 class="mb-0 text-info font-weight-bold text-left">1. General Information</h5>
                </template>
                <BRow class="text-left">
                    <BCol md="6">

                        <BFormGroup label="Guest Name *" class="mb-3">
                            <v-select :options="guestList" v-model="form.guest_uuid"
                                :reduce="(option) => option.uuid" 
                                :get-option-label="(option) => `[${option.alias || '-'}] ${option.fullname}`"
                                placeholder="Select Guest" 
                                :loading="loadingStates.guests" @open="loadGuests"
                                :class="{ 'is-invalid': v$.guest_uuid.$dirty && v$.guest_uuid.$invalid }" />
                            <BFormInvalidFeedback :state="!(v$.guest_uuid.$dirty && v$.guest_uuid.$invalid)">
                                {{ v$.guest_uuid.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>
                        
                        <!-- Offer Invoice Selection -->
                        <BFormGroup label="Invoice Tour *" class="mb-3">
                            <v-select :options="offerList" v-model="form.offer_uuid"
                                :reduce="(option) => option.uuid" 
                                :get-option-label="(option) => `${option.offer_no} / ${option.name}`"
                                placeholder="Select Offer"
                                :loading="loadingStates.offers" @open="loadOffers"
                                :disabled="isEdit"
                                @option:selected="onOfferSelected"
                                :class="{ 'is-invalid': v$.offer_uuid.$dirty && v$.offer_uuid.$invalid }" />
                            <BFormInvalidFeedback :state="!(v$.offer_uuid.$dirty && v$.offer_uuid.$invalid)">
                                {{ v$.offer_uuid.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>

                        
                        <BRow>

                            
                        <BCol md="6">

                            <!-- PAX -->
                            <BFormGroup label="Pax *" class="mb-3">
                                <BFormInput type="number" v-model="form.pax" placeholder="Total Guests"
                                    :state="v$.pax.$error ? false : null" />
                                    <div v-if="v$.pax.$error" class="text-danger small">
                                        {{ v$.pax.$errors[0].$message }}
                                    </div>
                                </BFormGroup>
                            </BCol>
                            <BCol md="6">
                        <!-- Transaction Date -->
                        <BFormGroup label="Transaction Date *" class="mb-3">
                            <BFormInput type="date" v-model="form.trans_date"
                                :state="v$.trans_date.$error ? false : null" />
                            <div v-if="v$.trans_date.$error" class="text-danger small">
                                {{ v$.trans_date.$errors[0].$message }}
                            </div>
                        </BFormGroup>

                        </BCol>
                        </BRow>
                    </BCol>

                    <BCol md="6">
                        <!-- Vehicle -->
                        <BFormGroup label="No. Polisi (Vehicle) *" class="mb-3">
                            <v-select label="label" :options="vehicleList" v-model="form.vehicle_uuid"
                                :reduce="(option) => option.uuid" placeholder="Select Vehicle"
                                :loading="loadingStates.vehicles" @open="loadVehicles"
                                :class="{ 'is-invalid': v$.vehicle_uuid.$dirty && v$.vehicle_uuid.$invalid }" />
                            <BFormInvalidFeedback :state="!(v$.vehicle_uuid.$dirty && v$.vehicle_uuid.$invalid)">
                                {{ v$.vehicle_uuid.$errors[0]?.$message }}
                            </BFormInvalidFeedback>
                        </BFormGroup>

                        <!-- Driver/Guide -->
                        <BRow>
                            <BCol md="4">
                                <BFormGroup label="Position" class="mb-3">
                                    <BFormSelect v-model="form.staff_position">
                                        <option value="Driver">Driver</option>
                                        <option value="Guide">Guide</option>
                                    </BFormSelect>
                                </BFormGroup>
                            </BCol>
                            <BCol md="8">
                                <BFormGroup label="Driver/Guide Name *" class="mb-3">
                                    <v-select label="fullname" :options="filteredStaffList" v-model="form.staff_uuid"
                                        :reduce="(option) => option.uuid" placeholder="Select Driver/Guide"
                                        :loading="loadingStates.staff" @open="loadStaff"
                                        :class="{ 'is-invalid': v$.staff_uuid.$dirty && v$.staff_uuid.$invalid }" />
                                    <BFormInvalidFeedback :state="!(v$.staff_uuid.$dirty && v$.staff_uuid.$invalid)">
                                        {{ v$.staff_uuid.$errors[0]?.$message }}
                                    </BFormInvalidFeedback>
                                </BFormGroup>
                            </BCol>
                        </BRow>

                        <!-- Dates -->
                        <BRow>
                            <BCol md="6">
                                <BFormGroup label="Start Date (Arrival) *" class="mb-3">
                                    <BFormInput type="date" v-model="form.start"
                                        :state="v$.start.$error ? false : null" />
                                    <div v-if="v$.start.$error" class="text-danger small">
                                        {{ v$.start.$errors[0].$message }}
                                    </div>
                                </BFormGroup>
                            </BCol>
                            <BCol md="6">
                                <BFormGroup label="End Date (Departure) *" class="mb-3">
                                    <BFormInput type="date" v-model="form.end" :state="v$.end.$error ? false : null" />
                                    <div v-if="v$.end.$error" class="text-danger small">
                                        {{ v$.end.$errors[0].$message }}
                                    </div>
                                </BFormGroup>
                            </BCol>
                        </BRow>
                    </BCol>
                </BRow>
            </BCard>
        </BCol>
    </BRow>

</template>

<script setup>
import { inject, ref, onMounted, reactive, nextTick, watch, computed } from "vue";
import { BRow, BCol, BFormGroup, BFormInput, BFormSelect, BCard, BFormInvalidFeedback } from "bootstrap-vue-next";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import vSelect from 'vue-select';

// Inject form and v$
const form = inject('form');
const v$ = inject('v$');
const isEdit = inject('isEdit');

const auth = useAuthStore();
const offerList = ref([]);
const guestList = ref([]);
const vehicleList = ref([]);
const staffList = ref([]);

const loadingStates = reactive({
    offers: false,
    guests: false,
    vehicles: false,
    staff: false
});

const isLoaded = reactive({
    offers: false,
    guests: false,
    vehicles: false,
    staff: false
});

const filteredStaffList = computed(() => {
    if (!form.staff_position) return staffList.value;
    return staffList.value.filter(staff => staff.position === form.staff_position);
});

// Lists loaders
const loadOffers = async () => {
    // Always reload to get fresh availability
    loadingStates.offers = true;
    try {
        const params = {};
        if (form.offer_uuid) {
            params.current_offer_uuid = form.offer_uuid;
        }
        if (form.guest_uuid) {
            params.guest_uuid = form.guest_uuid;
        }

        const res = await axios.get(`${process.env.VUE_APP_API_URL}/spj/available-offers`, {
            headers: { Authorization: `Bearer ${auth._token}` },
            params: params
        });
        if (res.data.status) offerList.value = res.data.data;
        isLoaded.offers = true;
    } catch (e) { console.error(e); }
    finally { loadingStates.offers = false; }
};

const loadGuests = async () => {
    if (isLoaded.guests) return;
    loadingStates.guests = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/guest`, {
            headers: { Authorization: `Bearer ${auth._token}` }
        });
        if (res.data.status) guestList.value = res.data.data;
        isLoaded.guests = true;
    } catch (e) { console.error(e); }
    finally { loadingStates.guests = false; }
};

const loadVehicles = async () => {
    if (isLoaded.vehicles) return;
    loadingStates.vehicles = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/vehicle`, {
            headers: { Authorization: `Bearer ${auth._token}` }
        });
        if (res.data.status) {
            vehicleList.value = res.data.data.map(v => ({
                uuid: v.uuid,
                label: `${v.plate_number} - ${v.name}`
            }));
            isLoaded.vehicles = true;
        }
    } catch (e) { console.error(e); }
    finally { loadingStates.vehicles = false; }
};

const loadStaff = async () => {
    if (isLoaded.staff) return;
    loadingStates.staff = true;
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/staff`, {
            headers: { Authorization: `Bearer ${auth._token}` }
        });
        if (res.data.status) staffList.value = res.data.data;
        isLoaded.staff = true;
    } catch (e) { console.error(e); }
    finally { loadingStates.staff = false; }
};

const swal = inject("$swal");
const previousOfferUuid = ref(null);

const onOfferSelected = async (option) => {
    await nextTick();
    if (!option) {
        previousOfferUuid.value = null;
        return;
    }
    const offerUuid = option.uuid || option;

    // Check if we need to warn (if programs exist)
    if (form.programs && form.programs.length > 0) {
        const confirm = await swal.fire({
            title: 'Load Invoice Tour?',
            text: 'This will replace your current itinerary and guest data.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Load',
            cancelButtonText: 'Cancel'
        });

        if (!confirm.isConfirmed) {
            // Revert selection
            form.offer_uuid = previousOfferUuid.value;
            return;
        }
    }

    // Update previous value to new confirmed value
    previousOfferUuid.value = offerUuid;
    form.offer_uuid = offerUuid;

    // fetch full offer details
    try {
        const res = await axios.get(`${process.env.VUE_APP_API_URL}/offer/detail/${offerUuid}`, {
            headers: { Authorization: `Bearer ${auth._token}` }
        });

        if (res.data.status) {
            const offer = res.data.data;

            // Auto-fill from offer
            if (offer.guest_uuid) {
                // Ensure guests are loaded to show label
                await loadGuests();
                form.guest_uuid = offer.guest_uuid; 
            } else if (offer.guest && offer.guest.uuid) {
                await loadGuests();
                form.guest_uuid = offer.guest.uuid;
            }
            
            if (offer.start) form.start = offer.start;
            if (offer.end) form.end = offer.end;
            if (offer.pax) form.pax = offer.pax;

            // Generate Programs from Offer Programs
            if (offer.programs && offer.programs.length > 0) {
                const newPrograms = offer.programs.map(p => {
                    // Route Name
                    const from = p.from_destination?.name || 'Unknown';
                    const to = p.to_destination?.name || 'Unknown';
                    const route = (from !== 'Unknown' || to !== 'Unknown') ? `${from} - ${to}` : '';

                    // Hotel Name (First accommodation if exists)
                    const hotel = (p.accommodations && p.accommodations.length > 0)
                        ? p.accommodations[0].name
                        : '';

                    // Date calculation if null
                    let dateDisplay = p.date;
                    if (!dateDisplay && offer.start && p.day) {
                        const d = new Date(offer.start);
                        d.setDate(d.getDate() + (p.day - 1));
                        dateDisplay = d.toISOString().split('T')[0];
                    }

                    return {
                        date: dateDisplay,
                        route_name: route,
                        route_uuid: p.matched_route ? p.matched_route.uuid : null,
                        distance: (p.matched_route && parseFloat(p.matched_route.distance) > 0) ? parseFloat(p.matched_route.distance) : "",
                        hotel_name: hotel,
                        amount: 0 // Default salary 0
                    };
                });
                form.programs = newPrograms;
            } else {
                // Fallback to empty dates if no programs found
                // generateTimeline(); // If we want to keep the old fallback
            }
        }
    } catch (e) {
        console.error("Error fetching offer details:", e);
    }
};

watch(() => form.offer_uuid, (newVal) => {
    if (newVal) {
        loadOffers();
        previousOfferUuid.value = newVal;
    }
}, { immediate: true });

watch(() => form.guest_uuid, (newVal) => {
    // Reload offers based on new guest (or all if null)
    loadOffers();
    
    // If we changed guest, and the currently selected offer does not belong to this guest, we should probably warn or clear?
    // But for now, let's just reload the list. If the current offer is valid for this guest, it stays. 
    // If not valid (filtered out), it might still show as selected value but not in list options.
    // Ideally, we might want to clear offer_uuid if it's not in the new filtered list, but let's keep it simple.
    
    if (newVal) loadGuests();
}, { immediate: true });

watch(() => form.vehicle_uuid, (newVal) => {
    if (newVal) loadVehicles();
}, { immediate: true });

watch(() => form.staff_uuid, (newVal) => {
    if (newVal) loadStaff();
}, { immediate: true });

watch(() => form.staff_position, (newVal) => {
    if (staffList.value.length === 0) return;
    
    if (form.staff_uuid) {
        const currentStaff = staffList.value.find(s => s.uuid === form.staff_uuid);
        if (currentStaff && currentStaff.position === newVal) {
            return;
        }
    }
    
    form.staff_uuid = null;
});

watch(filteredStaffList, (newVal) => {
    if (newVal.length === 1 && !isEdit.value) {
        form.staff_uuid = newVal[0].uuid;
    }
}, { immediate: true });

// Auto-filter programs if start or end date changes manually
watch([() => form.start, () => form.end], ([newStart, newEnd], [oldStart, oldEnd]) => {
    // Only auto-filter if it's not edit mode, and programs exist
    // We also check if oldStart and oldEnd are truthy to prevent filtering right on load
    if (!isEdit.value && form.programs && form.programs.length > 0 && oldStart && oldEnd) {
        if (newStart && newEnd) {
            form.programs = form.programs.filter(p => {
                if (!p.date) return true;
                return p.date >= newStart && p.date <= newEnd;
            });
        }
    }
});

onMounted(() => {
    // Initial setup if needed
});
</script>
